// src/context/userContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, arrayUnion, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase/firestore';
import { useLocation } from 'react-router-dom';

const UserContext = createContext();
export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [balance, setBalance] = useState(0);
  const [id, setId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTwo, setLoadingTwo] = useState(true);
  const [refBonus, setRefBonus] = useState(0);
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [premium, setPremium] = useState(false);
  const [error, setError] = useState(null);
  const [openInfoThree, setOpenInfoThree] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [telegramData, setTelegramData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [userType, setUserType] = useState(null); // 'telegram' or 'web'
  const location = useLocation();

  // Получаем Telegram пользователя
  const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  
  // Получаем веб-пользователя из Firebase Auth
  const auth = getAuth();
  const webAuthUser = auth.currentUser;

  // Функция для определения типа пользователя и его ID
  const getUserInfo = () => {
    if (telegramUser?.id) {
      return { id: telegramUser.id.toString(), type: 'telegram', user: telegramUser };
    }
    if (webAuthUser?.uid) {
      return { id: webAuthUser.uid, type: 'web', user: webAuthUser };
    }
    return null;
  };

  // Сохраняем данные из Telegram в состоянии
  useEffect(() => {
    if (telegramUser) {
      setTelegramData({
        id: telegramUser.id,
        username: telegramUser.username,
        firstName: telegramUser.first_name,
        lastName: telegramUser.last_name,
        isPremium: telegramUser.is_premium
      });
    }
  }, [telegramUser]);

  // ✅ Универсальная функция для определения premium статуса
  const determinePremiumStatus = (firestoreData) => {
    if (!firestoreData) return false;
    
    // Проверяем subscription.active - это самый надежный способ!
    if (firestoreData.subscription?.active === true) {
      console.log('✅ Premium detected: subscription.active = true');
      return true;
    }
    
    // Проверяем статус подписки
    if (firestoreData.subscription?.status === 'active' || 
        firestoreData.subscription?.status === 'trialing' ||
        firestoreData.subscription?.status === 'complete') {
      console.log('✅ Premium detected: subscription.status =', firestoreData.subscription.status);
      return true;
    }
    
    // Проверяем все возможные поля для premium (обратная совместимость)
    const isPremium = 
      firestoreData.premium === true ||
      firestoreData.Premium === true ||
      firestoreData.isPremium === true ||
      firestoreData.is_premium === true;
    
    if (isPremium) {
      console.log('✅ Premium detected from legacy fields');
      return true;
    }
    
    // Проверяем текущий период подписки
    if (firestoreData.subscription?.currentPeriodEnd) {
      const endDate = new Date(firestoreData.subscription.currentPeriodEnd);
      const now = new Date();
      if (endDate > now) {
        console.log('✅ Premium detected: active subscription period');
        return true;
      }
    }
    
    return false;
  };

  // Получение имени коллекции в зависимости от типа пользователя
  const getCollectionName = (type) => {
    return type === 'telegram' ? 'telegramUsers' : 'webUsers';
  };

  const fetchData = async (userId, type = null) => {
    if (!userId) {
      console.log('fetchData called with no userId');
      return;
    }

    const userType_local = type || userType;
    const collectionName = getCollectionName(userType_local);
    
    try {
      console.log('🔍 Fetching user data from:', collectionName, 'for:', userId);
      const userRef = doc(db, collectionName, userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        console.log('📦 User data found:', {
          premium: data.premium,
          subscription: data.subscription,
          hasSubscription: !!data.subscription,
          subscriptionActive: data.subscription?.active,
          subscriptionStatus: data.subscription?.status
        });
        
        // Определяем premium статус ТОЛЬКО из Firestore
        const isUserPremium = determinePremiumStatus(data);
        
        // Устанавливаем данные из Firestore
        setBalance(data.balance || 0);
        setRefBonus(data.refBonus || 0);
        setPremium(isUserPremium);
        setReferrals(data.referrals || []);
        
        // Сохраняем данные подписки если есть
        if (data.subscription) {
          setSubscriptionData(data.subscription);
          console.log('💳 Subscription data loaded:', {
            planName: data.subscription.planName,
            planId: data.subscription.planId,
            price: data.subscription.price,
            active: data.subscription.active,
            endDate: data.subscription.currentPeriodEnd
          });
        }
        
        // Устанавливаем username и fullName
        if (userType_local === 'telegram') {
          if (data.username || telegramUser?.username) {
            setUsername(data.username || telegramUser?.username || '');
          }
          
          if (data.fullName || telegramUser?.first_name || telegramUser?.last_name) {
            const fullNameFromTelegram = `${telegramUser?.first_name || ''} ${telegramUser?.last_name || ''}`.trim();
            setFullName(data.fullName || fullNameFromTelegram || '');
          }
        } else {
          // Для веб-пользователей
          setUsername(data.email || data.username || '');
          setFullName(data.fullName || data.displayName || '');
        }
        
        setId(userId);
        setUserType(userType_local);
        
        console.log('🎯 Premium status set to:', isUserPremium);
      } else {
        console.log('📝 User document does not exist for:', collectionName);
        if (userType_local === 'telegram') {
          await createUserFromTelegram(userId, telegramUser);
        } else {
          await createUserFromWeb(userId, webAuthUser);
        }
      }
    } catch (err) {
      console.error('❌ Ошибка при получении данных:', err);
      setError('Не удалось загрузить данных пользователя.');
    }

    setLoading(false);
  };

  // Создание пользователя из Telegram данных
  const createUserFromTelegram = async (userId, tgUser) => {
    if (!tgUser) return;

    try {
      const userRef = doc(db, 'telegramUsers', userId.toString());
      
      const finalUsername = tgUser.username || `${tgUser.first_name}_${userId}`;
      const fullNamed = `${tgUser.first_name || ''} ${tgUser.last_name || ''}`.trim();
      
      const userData = {
        userId: userId.toString(),
        username: finalUsername,
        firstName: tgUser.first_name || '',
        lastName: tgUser.last_name || '',
        fullName: fullNamed,
        balance: 0,
        refBonus: 0,
        premium: false,
        Premium: false,
        isPremium: false,
        is_premium: false,
        subscription: null,
        lastActive: new Date(),
        refereeId: null,
        referrals: [],
        favorites: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await setDoc(userRef, userData);
      console.log('✅ New Telegram user created in Firestore:', userId);
      
      setUsername(finalUsername);
      setFullName(fullNamed);
      setPremium(false);
      setSubscriptionData(null);
      setUserType('telegram');
      
    } catch (err) {
      console.error('❌ Error creating user from Telegram:', err);
    }
  };

  // Создание пользователя из веб-регистрации
  const createUserFromWeb = async (userId, webUser) => {
    if (!webUser) return;

    try {
      const userRef = doc(db, 'webUsers', userId);
      
      const userData = {
        userId: userId,
        email: webUser.email,
        fullName: webUser.displayName || '',
        username: webUser.email,
        balance: 0,
        premium: false,
        subscription: null,
        lastActive: new Date(),
        favorites: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await setDoc(userRef, userData);
      console.log('✅ New Web user created in Firestore:', userId);
      
      setUsername(webUser.email);
      setFullName(webUser.displayName || '');
      setPremium(false);
      setSubscriptionData(null);
      setUserType('web');
      
    } catch (err) {
      console.error('❌ Error creating user from Web:', err);
    }
  };

  // Real-time подписка на изменения
  useEffect(() => {
    if (!id || !userType) return;

    console.log('🔔 Setting up real-time listener for user:', id, 'type:', userType);
    const collectionName = getCollectionName(userType);
    const userRef = doc(db, collectionName, id);
    
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        const isUserPremium = determinePremiumStatus(data);
        
        if (premium !== isUserPremium) {
          console.log('🔄 Premium status changed in Firestore:', {
            from: premium,
            to: isUserPremium,
            subscription: data.subscription
          });
          setPremium(isUserPremium);
        }
        
        if (JSON.stringify(subscriptionData) !== JSON.stringify(data.subscription)) {
          console.log('🔄 Subscription data updated:', data.subscription);
          setSubscriptionData(data.subscription);
        }
        
        if (data.balance !== balance) {
          setBalance(data.balance || 0);
        }
        
        if (data.referrals && JSON.stringify(referrals) !== JSON.stringify(data.referrals)) {
          setReferrals(data.referrals);
        }
      }
    }, (error) => {
      console.error('❌ Error in real-time subscription:', error);
    });

    return () => {
      console.log('🔇 Unsubscribing from real-time listener');
      unsubscribe();
    };
  }, [id, userType, premium, subscriptionData, balance, referrals]);

  const sendUserData = async () => {
    console.log('📤 sendUserData called');
    
    const userInfo = getUserInfo();
    
    if (!userInfo) {
      console.log('⚠️ No user found (neither Telegram nor Web)');
      setLoading(false);
      setInitialized(true);
      return;
    }

    const { id: userId, type, user } = userInfo;
    console.log('👤 User found:', { userId, type });

    // Для Telegram пользователей
    if (type === 'telegram') {
      const queryParams = new URLSearchParams(window.location.search);
      let referrerId = queryParams.get("ref")?.replace(/\D/g, "");

      const tgUser = user;
      const finalUsername = tgUser.username || `${tgUser.first_name}_${userId}`;
      const fullNamed = `${tgUser.first_name || ''} ${tgUser.last_name || ''}`.trim();
      
      setUsername(finalUsername);
      setFullName(fullNamed);
      setId(userId);
      setUserType('telegram');

      const userRef = doc(db, 'telegramUsers', userId);
      
      try {
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
          await createUserFromTelegram(userId, tgUser);
          
          if (referrerId && referrerId !== userId) {
            try {
              const referrerRef = doc(db, 'telegramUsers', referrerId);
              const referrerDoc = await getDoc(referrerRef);
              if (referrerDoc.exists()) {
                await updateDoc(referrerRef, {
                  referrals: arrayUnion({
                    userId: userId,
                    username: finalUsername,
                    joinedAt: new Date(),
                    balance: 0
                  }),
                });
                console.log('✅ Referrer updated:', referrerId);
              }
            } catch (referrerErr) {
              console.error('❌ Error updating referrer:', referrerErr);
            }
          }
        } else {
          console.log('📝 User document already exists, updating lastActive');
          await updateDoc(userRef, {
            lastActive: new Date(),
            updatedAt: new Date(),
            ...(finalUsername && { username: finalUsername }),
            ...(fullNamed && { fullName: fullNamed })
          });
        }

        await fetchData(userId, 'telegram');
        
      } catch (err) {
        console.error('❌ Ошибка при создании/обновлении пользователя:', err);
        setError('Ошибка при сохранении данных пользователя.');
      } finally {
        setInitialized(true);
      }
    } 
    // Для веб-пользователей
    else if (type === 'web') {
      const webUserData = user;
      
      setUsername(webUserData.email);
      setFullName(webUserData.displayName || '');
      setId(userId);
      setUserType('web');

      const userRef = doc(db, 'webUsers', userId);
      
      try {
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
          await createUserFromWeb(userId, webUserData);
        } else {
          console.log('📝 Web user document exists, updating lastActive');
          await updateDoc(userRef, {
            lastActive: new Date(),
            updatedAt: new Date()
          });
        }

        await fetchData(userId, 'web');
        
      } catch (err) {
        console.error('❌ Ошибка при создании/обновлении веб-пользователя:', err);
        setError('Ошибка при сохранении данных пользователя.');
      } finally {
        setInitialized(true);
      }
    }
  };

  // Функция для принудительной проверки premium статуса
  const checkPremiumStatus = async () => {
    if (!id || !userType) {
      console.log('⚠️ No user ID/type available for premium check');
      return premium;
    }

    try {
      console.log('🔍 Checking premium status for user:', id, 'type:', userType);
      const collectionName = getCollectionName(userType);
      const userRef = doc(db, collectionName, id);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        const isUserPremium = determinePremiumStatus(data);
        
        console.log('📊 Premium status check result:', {
          userId: id,
          userType: userType,
          firestoreData: {
            subscription: data.subscription,
            subscriptionActive: data.subscription?.active,
            premium: data.premium
          },
          calculatedPremium: isUserPremium,
          currentStatePremium: premium
        });
        
        if (premium !== isUserPremium) {
          console.log('🔄 Updating premium status from', premium, 'to', isUserPremium);
          setPremium(isUserPremium);
        }
        
        return isUserPremium;
      }
    } catch (err) {
      console.error('❌ Error checking premium status:', err);
    }
    
    return premium;
  };

  // Функция для принудительной синхронизации с Firebase
  const syncWithFirebase = async () => {
    if (!id || !userType) return;
    
    setLoading(true);
    try {
      await fetchData(id, userType);
      console.log('✅ Data synced with Firebase');
    } finally {
      setLoading(false);
    }
  };

  // Функция для принудительной синхронизации premium статуса
  const syncPremiumStatus = async () => {
    setLoading(true);
    try {
      await checkPremiumStatus();
    } finally {
      setLoading(false);
    }
  };

  // Периодическая проверка premium статуса (каждые 2 минуты)
  useEffect(() => {
    if (!id) return;

    const interval = setInterval(() => {
      console.log('⏰ Periodic premium status check');
      checkPremiumStatus();
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, [id, userType]);

  useEffect(() => {
    console.log('🚀 UserProvider mounted');
    sendUserData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (id && userType) {
      console.log('🆔 User ID/Type set/changed:', id, userType);
      fetchData(id, userType);
    }
  }, [id, userType]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.log('⏱️ Loading timeout reached');
        setLoading(false);
      }
    }, 3000);
    
    return () => clearTimeout(timeout);
  }, [loading]);

  // Функция для обновления данных пользователя в Firestore
  const updateUserDataInFirestore = async (userId, data, type = null) => {
    const userType_local = type || userType;
    if (!userType_local) return;
    
    try {
      const collectionName = getCollectionName(userType_local);
      const userRef = doc(db, collectionName, userId);
      await updateDoc(userRef, {
        ...data,
        updatedAt: new Date()
      });
      console.log('✅ User data updated in Firestore');
    } catch (err) {
      console.error('❌ Error updating user data in Firestore:', err);
    }
  };

  return (
    <UserContext.Provider value={{
      balance, setBalance,
      id,
      loading, setLoading,
      loadingTwo, setLoadingTwo,
      refBonus, setRefBonus,
      telegramUser,
      fullName, setFullName,
      username, setUsername,
      premium, setPremium,
      error, setError,
      location,
      openInfoThree, setOpenInfoThree,
      initialized,
      referrals, setReferrals,
      subscriptionData, setSubscriptionData,
      refreshUserData: syncWithFirebase,
      telegramData,
      checkPremiumStatus,
      syncPremiumStatus,
      syncWithFirebase,
      updateUserDataInFirestore,
      userType
    }}>
      {children}
    </UserContext.Provider>
  );
};